import { tasksApi } from './client'

// ── Types ─────────────────────────────────────────────────────────────────────

export type TaskStatus   = 'Todo' | 'In Progress' | 'Review' | 'Done' | 'Blocked'
export type TaskPriority = 'Low' | 'Medium' | 'High' | 'Critical'
export type TaskType     = 'Task' | 'Bug' | 'Feature' | 'Story' | 'Improvement' | 'Chore'

export interface Task {
  id:             string
  taskNo:         string
  title:          string
  description?:   string
  status:         TaskStatus
  priority:       TaskPriority
  type:           TaskType
  assignee?:      string
  assigneeName?:  string
  assignees?:     TaskAssignee[]
  reporter?:      string
  reporterName?:  string
  project?:       string
  projectName?:   string
  sprint?:        string
  sprintName?:    string
  labels?:        string[]
  startDate?:     string
  dueDate?:       string
  estimatedHours?: number
  remainingHours?: number
  skillRequired?: string
  billable?:      boolean
  loggedHours?:   number
  commentCount?:  number
  checklistCount?: number
  checklistDone?:  number
  attachmentCount?: number
  createdAt:      string
  updatedAt:      string
}

export interface TaskPayload {
  title:          string
  description?:   string
  status:         TaskStatus
  priority:       TaskPriority
  type:           TaskType
  assignee?:      string
  assigneeName?:  string
  assignees?:     TaskAssigneeInput[]
  project?:       string
  projectName?:   string
  sprint?:        string
  sprintName?:    string
  labels?:        string[]
  startDate?:     string
  dueDate?:       string
  estimatedHours?: number
  remainingHours?: number
  skillRequired?: string
  billable?:      boolean
}

export interface TaskFilters {
  status?:   TaskStatus
  priority?: TaskPriority
  type?:     TaskType
  assignee?:  string
  assignees?: string[]
  project?:   string
  sprint?:    string
  label?:     string
  labels?:    string[]
  search?:   string
  limit?:    number
  offset?:   number
}

export interface TaskListResponse {
  data:   Task[]
  total:  number
  limit:  number
  offset: number
}

export interface TaskMember {
  id:        string
  email?:    string
  name?:     string
  avatarUrl?: string
}

export type BoardVisibility = 'organization' | 'private' | 'restricted'
export type BoardRole = 'owner' | 'manager' | 'editor' | 'commenter' | 'viewer'

export interface TaskProject {
  id:     string
  name:   string
  description?: string
  color?: string
  visibility?: BoardVisibility
  ownerId?: string
  owner_id?: string
  currentUserRole?: BoardRole
  allowExternalAssignees?: boolean
  allow_external_assignees?: boolean
  memberCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface BoardMember {
  id: string
  boardId: string
  userId: string
  name?: string
  email?: string
  role: BoardRole
  addedBy?: string
  addedAt?: string
  updatedAt?: string
}

export interface BoardMembersResponse {
  boardId: string
  visibility: BoardVisibility
  ownerId?: string
  members: BoardMember[]
}

export interface BoardAccess {
  canView: boolean
  canEdit: boolean
  canManageMembers: boolean
  canCreateTask?: boolean
  canAssignTasks: boolean
  canLogTime: boolean
  canViewPlanning: boolean
}

export interface CreateProjectPayload {
  name: string
  description?: string
  visibility: Exclude<BoardVisibility, 'restricted'>
  members?: Array<{ userId: string; role: Exclude<BoardRole, 'owner'> }>
  allowExternalAssignees?: boolean
}

export interface UpdateProjectPayload {
  name?: string
  description?: string
  visibility?: Exclude<BoardVisibility, 'restricted'>
  allowExternalAssignees?: boolean
}

export interface TaskSprint {
  id:        string
  name:      string
  projectId: string
  startDate?: string
  endDate?:   string
  active?:    boolean
}


export interface TaskAssignee {
  id: string
  taskId: string
  userId: string
  userName?: string
  userEmail?: string
  role: string
  createdAt: string
}

export interface TaskAssigneeInput {
  userId: string
  userName?: string
  userEmail?: string
  role?: string
}

export interface TaskChecklistItem {
  id: string
  taskId: string
  title: string
  completed: boolean
  position: number
  createdBy?: string
  completedBy?: string
  completedAt?: string
  createdAt: string
  updatedAt: string
}

export interface TaskDependency {
  id: string
  taskId: string
  dependsOnTaskId: string
  dependencyType: string
  createdBy?: string
  createdAt: string
}

export interface TaskAttachment {
  id: string
  taskId: string
  fileName: string
  contentType?: string
  sizeBytes: number
  objectKey?: string
  url?: string
  uploadedBy?: string
  createdAt: string
}

export interface TaskActivityLog {
  id: string
  taskId: string
  actorId?: string
  action: string
  field?: string
  oldValue?: string
  newValue?: string
  createdAt: string
}


export type WorkloadStatus = 'Underloaded' | 'Properly loaded' | 'Slightly overloaded' | 'Overloaded' | 'Unavailable'

export interface DailyWorkload { date: string; availableHours: number; allocatedHours: number; loggedHours: number; remainingHours: number; loadPercentage?: number; workloadStatus?: WorkloadStatus; overCapacityHours?: number; freeCapacityHours?: number }
export interface WorkloadTaskRow { taskId: string; taskNo: string; title: string; status: string; projectName?: string; estimatedHours?: number; allocatedHours: number; allocatedHoursInRange?: number; loggedHours: number; loggedHoursInRange?: number; remainingHours: number; workRemainingHours?: number; varianceHours?: number; varianceLabel?: string; estimateHealth?: string; warnings?: string[]; startDate?: string; dueDate?: string; needsEstimate: boolean; unscheduled: boolean; skillRequired?: string; billable: boolean }
export interface WorkloadSuggestion { taskId: string; taskTitle: string; moveHours: number; suggestedUserId: string; suggestedUserName: string; suggestedUserFreeCapacityHours: number; reason: string }
export interface UserWorkload { userId: string; userName: string; availableHours: number; allocatedHours: number; loggedHours: number; remainingHours: number; loadPercentage: number; workloadStatus: WorkloadStatus; overloadedByHours: number; overCapacityHours?: number; freeCapacity: number; freeCapacityHours?: number; dailyBreakdown: DailyWorkload[]; tasks: WorkloadTaskRow[]; suggestions?: WorkloadSuggestion[] }
export interface SuggestedUser { userId: string; userName: string; freeCapacity: number; loadPercentage: number; workloadStatus: string; matchesSkill: boolean }
export interface CapacityCheck { userId: string; before: UserWorkload; after: UserWorkload; warning?: string; suggestedAssignees: SuggestedUser[] }
export interface WorkloadReport { estimatedVsActualByTask: WorkloadTaskRow[]; estimatedVsActualByUser: UserWorkload[]; loggedHoursByDate: DailyWorkload[]; overloadedResources: UserWorkload[]; underloadedResources: UserWorkload[]; tasksMissingEstimate?: number; unscheduledTasks?: number; tasksOverEstimate?: number; timeEntriesLogged?: number }
export interface TimeEntry { id: string; taskId: string; createdBy?: string; projectId?: string; date: string; hours: number; description?: string; billable: boolean; createdAt: string; updatedAt: string }

export type TimesheetSubmissionStatus = 'submitted' | 'approved' | 'rejected'
export interface TimesheetSubmission {
  id: string
  orgId: string
  userId: string
  weekStartDate: string
  weekEndDate: string
  status: TimesheetSubmissionStatus
  totalHours: number
  submittedAt: string
  reviewedBy?: string
  reviewedAt?: string
  reviewNote?: string
  createdAt: string
  updatedAt: string
  userDisplayName?: string
}
export type TimesheetTeamMemberStatus = 'not_submitted' | TimesheetSubmissionStatus
export interface TimesheetTeamSummaryRow {
  userId: string
  displayName: string
  totalHours: number
  status: TimesheetTeamMemberStatus
  submissionId?: string
}
export interface ResourceAvailability { id?: string; userId?: string; dayOfWeek: number; availableHours: number; isWorkingDay: boolean }

export interface TaskComment {
  id:          string
  taskId:      string
  content:     string
  authorId?:   string
  authorName?: string
  createdAt:   string
}

// ── Config ────────────────────────────────────────────────────────────────────

export const TASK_STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bg: string; text: string; hex: string }> = {
  'Todo':        { label: 'Todo',        color: 'slate',  bg: 'bg-bg-muted',         text: 'text-text-tertiary', hex: '#64748b' },
  'In Progress': { label: 'In Progress', color: 'blue',   bg: 'bg-blue-500/10',      text: 'text-blue-500',      hex: '#3b82f6' },
  'Review':      { label: 'Review',      color: 'violet', bg: 'bg-violet-500/10',    text: 'text-violet-500',    hex: '#8b5cf6' },
  'Done':        { label: 'Done',        color: 'green',  bg: 'bg-green-500/10',     text: 'text-green-500',     hex: '#22c55e' },
  'Blocked':     { label: 'Blocked',     color: 'red',    bg: 'bg-status-error/10',  text: 'text-status-error',  hex: '#ef4444' },
}

export const TASK_PRIORITY_CONFIG: Record<TaskPriority, { label: string; dot: string; cls: string }> = {
  Low:      { label: 'Low',      dot: 'bg-slate-400',     cls: 'text-slate-400  bg-slate-400/10'   },
  Medium:   { label: 'Medium',   dot: 'bg-amber-400',     cls: 'text-amber-500  bg-amber-400/10'   },
  High:     { label: 'High',     dot: 'bg-orange-500',    cls: 'text-orange-500 bg-orange-500/10'  },
  Critical: { label: 'Critical', dot: 'bg-status-error',  cls: 'text-status-error bg-status-error/10' },
}

export const TASK_TYPE_CONFIG: Record<TaskType, { icon: string; color: string }> = {
  Task:        { icon: '☑',  color: '#3b82f6' },
  Bug:         { icon: '🐛', color: '#ef4444' },
  Feature:     { icon: '✨', color: '#8b5cf6' },
  Story:       { icon: '📖', color: '#f59e0b' },
  Improvement: { icon: '⬆', color: '#10b981' },
  Chore:       { icon: '🔧', color: '#6b7280' },
}

// Kanban columns — Tasks has its own status set, independent of tickets
export const TASK_COLUMNS: { id: TaskStatus; label: string; color: string; bg: string }[] = [
  { id: 'Todo',        label: 'To Do',       color: '#64748b', bg: 'bg-slate-500/5'   },
  { id: 'In Progress', label: 'In Progress', color: '#3b82f6', bg: 'bg-blue-500/5'    },
  { id: 'Review',      label: 'Review',      color: '#8b5cf6', bg: 'bg-violet-500/5'  },
  { id: 'Done',        label: 'Done',        color: '#22c55e', bg: 'bg-green-500/5'   },
  { id: 'Blocked',     label: 'Blocked',     color: '#ef4444', bg: 'bg-red-500/5'     },
]

// ── API client ────────────────────────────────────────────────────────────────

const BASE = '/api/v1/tasks'
const TIMESHEET_BASE = '/api/v1/timesheet-submissions'

function buildQuery(filters: TaskFilters): string {
  const p = new URLSearchParams()
  if (filters.status)   p.set('status',   filters.status)
  if (filters.priority) p.set('priority', filters.priority)
  if (filters.type)     p.set('type',     filters.type)
  if (filters.assignee) p.append('assignee', filters.assignee)
  filters.assignees?.forEach(v => p.append('assignee', v))
  if (filters.project)  p.set('project',  filters.project)
  if (filters.sprint)   p.set('sprint',   filters.sprint)
  if (filters.label)    p.append('label', filters.label)
  filters.labels?.forEach(v => p.append('label', v))
  if (filters.search)   p.set('search',   filters.search)
  if (filters.limit)    p.set('limit',    String(filters.limit))
  if (filters.offset)   p.set('offset',   String(filters.offset))
  const q = p.toString()
  return q ? `?${q}` : ''
}

function unwrapWorkloadRows(raw: unknown): UserWorkload[] {
  const value = raw as Record<string, unknown> | UserWorkload[] | UserWorkload | null | undefined
  let rows: unknown[]
  if (Array.isArray(value)) rows = value
  else if (value && typeof value === "object" && Array.isArray((value as Record<string, unknown>).resources)) rows = (value as Record<string, unknown>).resources as unknown[]
  else if (value && typeof value === "object" && Array.isArray((value as Record<string, unknown>).data)) rows = (value as Record<string, unknown>).data as unknown[]
  else if (value && typeof value === "object" && Array.isArray((value as Record<string, unknown>).result)) rows = (value as Record<string, unknown>).result as unknown[]
  else if (value && typeof value === "object" && ((value as Record<string, unknown>).userId || (value as Record<string, unknown>).userName)) rows = [value]
  else rows = []

  return rows.map(row => {
    const r = (row ?? {}) as Record<string, unknown>
    const dailyBreakdown = Array.isArray(r.dailyBreakdown) ? r.dailyBreakdown : []
    const tasks = Array.isArray(r.tasks) ? r.tasks : []
    const suggestions = Array.isArray(r.suggestions) ? r.suggestions : []
    return {
      ...(r as unknown as UserWorkload),
      userId: String(r.userId ?? r.user_id ?? r.email ?? ""),
      userName: String(r.userName ?? r.user_name ?? r.name ?? r.userId ?? ""),
      availableHours: Number(r.availableHours ?? r.available_hours ?? 0),
      allocatedHours: Number(r.allocatedHours ?? r.allocated_hours ?? 0),
      loggedHours: Number(r.loggedHours ?? r.logged_hours ?? 0),
      remainingHours: Number(r.remainingHours ?? r.workRemainingHours ?? r.remaining_hours ?? 0),
      loadPercentage: Number(r.loadPercentage ?? r.load_percentage ?? 0),
      workloadStatus: (r.workloadStatus ?? r.status ?? "Unavailable") as WorkloadStatus,
      overloadedByHours: Number(r.overloadedByHours ?? r.overloaded_by_hours ?? r.overCapacityHours ?? 0),
      overCapacityHours: Number(r.overCapacityHours ?? r.over_capacity_hours ?? r.overloadedByHours ?? 0),
      freeCapacity: Number(r.freeCapacity ?? r.freeCapacityHours ?? r.free_capacity_hours ?? 0),
      freeCapacityHours: Number(r.freeCapacityHours ?? r.free_capacity_hours ?? r.freeCapacity ?? 0),
      dailyBreakdown,
      tasks,
      suggestions,
    }
  })
}

export const taskService = {
  list: (filters: TaskFilters = {}): Promise<TaskListResponse> =>
    tasksApi.get<TaskListResponse>(`${BASE}${buildQuery(filters)}`),

  get: (id: string): Promise<Task> =>
    tasksApi.get<Task>(`${BASE}/${id}`),

  create: (payload: TaskPayload): Promise<Task> =>
    tasksApi.post<Task>(BASE, payload),

  update: (id: string, payload: Partial<TaskPayload>): Promise<Task> =>
    tasksApi.put<Task>(`${BASE}/${id}`, payload),

  delete: (id: string): Promise<void> =>
    tasksApi.delete<void>(`${BASE}/${id}`),

  updateStatus: (id: string, status: TaskStatus): Promise<Task> =>
    tasksApi.put<Task>(`${BASE}/${id}/status`, { status }),

  getNextNo: (): Promise<{ currentTaskNo: string; nextTaskNo: string }> =>
    tasksApi.get(`${BASE}/id-increment`),

  getMembers: (): Promise<TaskMember[]> =>
    tasksApi.get(`${BASE}/members`),

  getProjects: (): Promise<TaskProject[]> =>
    tasksApi.get(`${BASE}/projects`),

  getProject: (id: string): Promise<TaskProject> =>
    tasksApi.get(`${BASE}/projects/${encodeURIComponent(id)}`),

  createProject: (payload: CreateProjectPayload): Promise<TaskProject> =>
    tasksApi.post(`${BASE}/projects`, payload),

  updateProject: (id: string, payload: UpdateProjectPayload): Promise<TaskProject> =>
    tasksApi.patch(`${BASE}/projects/${encodeURIComponent(id)}`, payload),

  updateProjectVisibility: (id: string, visibility: Exclude<BoardVisibility, 'restricted'>): Promise<TaskProject> =>
    tasksApi.patch(`${BASE}/projects/${encodeURIComponent(id)}/visibility`, { visibility }),

  transferProjectOwner: (id: string, userId: string): Promise<TaskProject> =>
    tasksApi.patch(`${BASE}/projects/${encodeURIComponent(id)}/transfer-owner`, { userId }),

  getProjectMembers: (id: string): Promise<BoardMembersResponse> =>
    tasksApi.get(`${BASE}/projects/${encodeURIComponent(id)}/members`),

  addProjectMember: (id: string, payload: { userId: string; role: Exclude<BoardRole, 'owner'> }): Promise<BoardMember> =>
    tasksApi.post(`${BASE}/projects/${encodeURIComponent(id)}/members`, payload),

  updateProjectMember: (id: string, memberId: string, payload: { role: Exclude<BoardRole, 'owner'> }): Promise<BoardMember> =>
    tasksApi.patch(`${BASE}/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}`, payload),

  removeProjectMember: (id: string, memberId: string, payload?: { reassignTo?: string; keepHistoricalAssignments?: boolean }): Promise<void> =>
    tasksApi.delete(`${BASE}/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}${payload?.reassignTo ? `?reassignTo=${encodeURIComponent(payload.reassignTo)}` : payload?.keepHistoricalAssignments ? '?keepHistoricalAssignments=true' : ''}`),

  getProjectAccess: (id: string): Promise<BoardAccess> =>
    tasksApi.get(`${BASE}/projects/${encodeURIComponent(id)}/access`),

  getSprints: (project?: string): Promise<TaskSprint[]> =>
    tasksApi.get(`${BASE}/sprints${project ? `?project=${project}` : ''}`),

  getComments: (id: string): Promise<TaskComment[]> =>
    tasksApi.get(`${BASE}/${id}/comments`),

  addComment: (id: string, content: string): Promise<TaskComment> =>
    tasksApi.post(`${BASE}/${id}/comments`, { content }),

  logTime: (id: string, hours: number, description?: string, date?: string, billable = false): Promise<Task> =>
    tasksApi.post(`${BASE}/${id}/log-time`, { hours, description, date, billable }),

  listTimeEntries: (filters: { taskId?: string; userId?: string; projectId?: string; startDate?: string; endDate?: string } = {}): Promise<TimeEntry[]> => {
    const q = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v) })
    return tasksApi.get(`${BASE}/time-entries${q.toString() ? `?${q}` : ''}`)
  },

  createTimeEntry: (entry: Partial<TimeEntry> & { taskId: string; hours: number; date: string }): Promise<TimeEntry> =>
    tasksApi.post(`${BASE}/time-entries`, entry),

  updateTimeEntry: (id: string, entry: Partial<TimeEntry> & { hours: number; date: string }): Promise<TimeEntry> =>
    tasksApi.put(`${BASE}/time-entries/${id}`, entry),

  deleteTimeEntry: (id: string): Promise<void> =>
    tasksApi.delete(`${BASE}/time-entries/${id}`),

  submitTimesheet: (weekStart: string): Promise<TimesheetSubmission> =>
    tasksApi.post(TIMESHEET_BASE, { weekStart }),

  getMyTimesheetSubmission: (weekStart: string): Promise<TimesheetSubmission | null> =>
    tasksApi.get(`${TIMESHEET_BASE}?scope=mine&weekStart=${weekStart}`),

  listTimesheetApprovals: (status?: string): Promise<TimesheetSubmission[]> =>
    tasksApi.get(`${TIMESHEET_BASE}${status ? `?status=${status}` : ''}`),

  getTimesheetTeamSummary: (weekStart: string): Promise<TimesheetTeamSummaryRow[]> =>
    tasksApi.get(`${TIMESHEET_BASE}/team-summary?weekStart=${weekStart}`),

  approveTimesheetSubmission: (id: string): Promise<TimesheetSubmission> =>
    tasksApi.post(`${TIMESHEET_BASE}/${id}/approve`, {}),

  rejectTimesheetSubmission: (id: string, note: string): Promise<TimesheetSubmission> =>
    tasksApi.post(`${TIMESHEET_BASE}/${id}/reject`, { note }),

  getAssignees: (id: string): Promise<TaskAssignee[]> =>
    tasksApi.get(`${BASE}/${id}/assignees`),

  replaceAssignees: (id: string, assignees: TaskAssigneeInput[]): Promise<TaskAssignee[]> =>
    tasksApi.put(`${BASE}/${id}/assignees`, { assignees }),

  getChecklist: (id: string): Promise<TaskChecklistItem[]> =>
    tasksApi.get(`${BASE}/${id}/checklist`),

  addChecklistItem: (id: string, title: string): Promise<TaskChecklistItem> =>
    tasksApi.post(`${BASE}/${id}/checklist`, { title }),

  updateChecklistItem: (id: string, itemId: string, patch: Partial<Pick<TaskChecklistItem, 'title' | 'completed'>>): Promise<TaskChecklistItem> =>
    tasksApi.put(`${BASE}/${id}/checklist/${itemId}`, patch),

  deleteChecklistItem: (id: string, itemId: string): Promise<void> =>
    tasksApi.delete(`${BASE}/${id}/checklist/${itemId}`),

  getDependencies: (id: string): Promise<TaskDependency[]> =>
    tasksApi.get(`${BASE}/${id}/dependencies`),

  addDependency: (id: string, dependsOnTaskId: string, dependencyType = 'blocks'): Promise<TaskDependency> =>
    tasksApi.post(`${BASE}/${id}/dependencies`, { dependsOnTaskId, dependencyType }),

  deleteDependency: (id: string, dependencyId: string): Promise<void> =>
    tasksApi.delete(`${BASE}/${id}/dependencies/${dependencyId}`),

  getAttachments: (id: string): Promise<TaskAttachment[]> =>
    tasksApi.get(`${BASE}/${id}/attachments`),

  addAttachment: (id: string, attachment: Pick<TaskAttachment, 'fileName'> & Partial<TaskAttachment>): Promise<TaskAttachment> =>
    tasksApi.post(`${BASE}/${id}/attachments`, attachment),

  deleteAttachment: (id: string, attachmentId: string): Promise<void> =>
    tasksApi.delete(`${BASE}/${id}/attachments/${attachmentId}`),

  getActivity: (id: string): Promise<TaskActivityLog[]> =>
    tasksApi.get(`${BASE}/${id}/activity`),

  getWorkload: (filters: { startDate: string; endDate: string; userId?: string[]; status?: string; projectId?: string }): Promise<UserWorkload[]> => {
    const q = new URLSearchParams()
    q.set('startDate', filters.startDate); q.set('endDate', filters.endDate)
    filters.userId?.forEach(id => q.append('userId', id))
    if (filters.status) q.set('status', filters.status)
    if (filters.projectId) q.set('project', filters.projectId)
    return tasksApi.get(`${BASE}/planning/workload?${q}`).then(unwrapWorkloadRows)
  },

  getUserWorkload: (userId: string, startDate: string, endDate: string): Promise<UserWorkload> =>
    tasksApi.get(`${BASE}/planning/workload/${encodeURIComponent(userId)}?startDate=${startDate}&endDate=${endDate}`),

  checkAssignmentCapacity: (payload: TaskPayload, suggestedUserIds: string[] = []): Promise<CapacityCheck> => {
    const q = new URLSearchParams()
    suggestedUserIds.forEach(id => q.append('suggestUserId', id))
    return tasksApi.post(`${BASE}/planning/check-assignment${q.toString() ? `?${q}` : ''}`, payload)
  },

  getSuggestedAssignees: (filters: { startDate: string; endDate: string; estimatedHours: number; skillRequired?: string; userId?: string[]; projectId?: string }): Promise<SuggestedUser[]> => {
    const q = new URLSearchParams()
    q.set('startDate', filters.startDate); q.set('endDate', filters.endDate); q.set('estimatedHours', String(filters.estimatedHours))
    if (filters.skillRequired) q.set('skillRequired', filters.skillRequired)
    filters.userId?.forEach(id => q.append('userId', id))
    if (filters.projectId) q.set('project', filters.projectId)
    return tasksApi.get(`${BASE}/planning/suggested-assignees?${q}`)
  },

  getPlanningReports: (startDate: string, endDate: string, projectId?: string): Promise<WorkloadReport> =>
    tasksApi.get(`${BASE}/planning/reports?startDate=${startDate}&endDate=${endDate}${projectId ? `&project=${encodeURIComponent(projectId)}` : ''}`),

  updateAvailability: (userId: string, availability: ResourceAvailability[]): Promise<ResourceAvailability[]> =>
    tasksApi.put(`${BASE}/planning/availability/${encodeURIComponent(userId)}`, { availability }),

  updatePlanning: (id: string, payload: Partial<TaskPayload>): Promise<Task> =>
    tasksApi.patch(`${BASE}/${id}/planning`, payload),
}
